function getShippingProtectionRules() {
  const dataEl = document.getElementById('ShippingProtectionRulesData');
  if (!dataEl) return [];
  try {
    return JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('Failed parsing shipping protection rules', e);
    return [];
  }
}

function findMatchingRule(subtotalCents, rules) {
  const exactMatch = rules.find(
    (rule) => subtotalCents >= rule.min_cents && subtotalCents <= rule.max_cents
  );
  if (exactMatch) return exactMatch;

  const highestMaxCents = Math.max(...rules.map((r) => r.max_cents));
  if (subtotalCents > highestMaxCents) {
    return rules.reduce((highest, rule) =>
      rule.max_cents > highest.max_cents ? rule : highest
    );
  }

  return null;
}

function formatShippingProtectionPrice(cents) {
  if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.theme && theme.moneyFormat) {
    return Shopify.formatMoney(cents, theme.moneyFormat);
  }
  // fallback kalau Shopify.formatMoney / theme.moneyFormat gak tersedia
  return 'Rp' + Math.round(cents / 100).toLocaleString('id-ID');
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindShippingProtectionButton();
    this.updateShippingProtectionPrice();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  updateShippingProtectionPrice() {
    // Scoped ke dalam drawer ini aja (this, bukan document),
    // biar gak ketuker kalau ada elemen sama di luar drawer (mis. halaman /cart)
    const btn = this.querySelector('.checkout-with-shipping-protection');
    if (!btn) return;

    const priceEl = btn.querySelector('.pricing-detail');
    if (!priceEl) return;

    const rules = getShippingProtectionRules();
    if (!rules.length) {
      priceEl.textContent = '';
      delete priceEl.dataset.originalPriceCents;
      return;
    }

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const protectionVariantIds = rules.map((r) => String(r.variant_id));

        const subtotalCents = cart.items.reduce((sum, item) => {
          if (protectionVariantIds.includes(String(item.variant_id))) return sum;
          return sum + item.line_price;
        }, 0);

        const matchingRule = findMatchingRule(subtotalCents, rules);

        if (!matchingRule || typeof matchingRule.price_cents !== 'number' || isNaN(matchingRule.price_cents)) {
          priceEl.textContent = '';
          delete priceEl.dataset.originalPriceCents;
          return;
        }

        // Tandai elemen ini sebagai harga yang perlu dikonversi mata uang.
        // CurrencyConverter mencari elemen lewat selector [data-original-price-cents],
        // jadi begitu attribute ini ke-set, elemen ini otomatis ikut ke-convert
        // setiap kali convertAllPrices() dipanggil (ganti currency, cart update, dst).
        priceEl.dataset.originalPriceCents = matchingRule.price_cents;

        const converter = window.currencyConverterInstance;
        if (converter && converter.ready) {
          converter.convertAllPrices();
        } else {
          // Fallback sementara: CurrencyConverter belum ready atau fitur convert nonaktif.
          priceEl.textContent =  '+' + formatShippingProtectionPrice(matchingRule.price_cents);
        }
      })
      .catch((error) => {
        console.error('Failed to update shipping protection price', error);
        priceEl.textContent = '';
        delete priceEl.dataset.originalPriceCents;
      });
  }

  bindShippingProtectionButton() {
    document.addEventListener('click', (event) => {
      const btnCheckoutShipping = event.target.closest('.checkout-with-shipping-protection');
      if (!btnCheckoutShipping || btnCheckoutShipping.disabled) return;

      const rules = getShippingProtectionRules();
      if (!rules.length) {
        console.error('No shipping protection rules found');
        return;
      }

      btnCheckoutShipping.disabled = true;
      btnCheckoutShipping.classList.add('loading');

      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          const protectionVariantIds = rules.map((r) => String(r.variant_id));

          const subtotalCents = cart.items.reduce((sum, item) => {
            if (protectionVariantIds.includes(String(item.variant_id))) return sum;
            return sum + item.line_price;
          }, 0);

          const matchingRule = findMatchingRule(subtotalCents, rules);

          if (!matchingRule) {
            console.error('No matching shipping protection rule for subtotal', subtotalCents);
            btnCheckoutShipping.disabled = false;
            btnCheckoutShipping.classList.remove('loading');
            return;
          }

          // Ambil SEMUA protection item yang ada di cart, bukan cuma satu
          const existingProtectionItems = cart.items.filter((item) =>
            protectionVariantIds.includes(String(item.variant_id))
          );

          // Kalau cuma ada 1 protection item DAN sudah sesuai tier yang benar → langsung checkout
          const alreadyCorrect =
            existingProtectionItems.length === 1 &&
            String(existingProtectionItems[0].variant_id) === String(matchingRule.variant_id);

          if (alreadyCorrect) {
            window.location.href = '/checkout';
            return;
          }

          // Hapus SEMUA protection item lama sekaligus (menutup celah kalau ada duplikat/tier ganda)
          let removeOldTiers = Promise.resolve();
          if (existingProtectionItems.length) {
            const updates = existingProtectionItems.reduce((acc, item) => {
              acc[item.variant_id] = 0;
              return acc;
            }, {});

            removeOldTiers = fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates }),
            }).then((response) => {
              if (!response.ok) {
                throw new Error('Failed to remove old shipping protection tier(s)');
              }
              return response.json();
            });
          }

          return removeOldTiers
            .then(() =>
              fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: matchingRule.variant_id, quantity: 1 }] }),
              })
            )
            .then((response) => {
              if (!response.ok) {
                return response.json().then((err) => {
                  throw new Error(err.description || 'Failed to add shipping protection to cart');
                });
              }
              return response.json();
            })
            .then(() => {
              window.location.href = '/checkout';
            });
        })
        .catch((error) => {
          console.error('Error processing shipping protection:', error);
          btnCheckoutShipping.disabled = false;
          btnCheckoutShipping.classList.remove('loading');
          alert('Gagal memproses shipping protection. Silakan coba lagi.');
        });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.updateShippingProtectionPrice();

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.updateShippingProtectionPrice();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);